import { HelpTip } from '../../../../components/HelpTip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput } from '../../../../components/inline-add-table/cells';
import {
  blankItemNumberRow,
  ITEM_NUMBERS_MAX,
  itemNumberProblem,
  type ItemNumberRow,
} from '../utils/itemNumbers';

/**
 * What the slots grid is, said once — in the HelpTip beside its label and in the DOM for a
 * reader who cannot reach a tooltip.
 */
const SLOTS_HELP =
  'One row per item — one person each. Type the item number off the plantilla; it must not be ' +
  'used by any other item, including one that was removed.';

/**
 * The slots grid: one row per item, each carrying the item number the reader TYPES.
 *
 * Shared by the New plantilla form and Add slots. Cell ids are `${idPrefix}-item-numbers[<row>]`
 * — the server's own spelling of a refused row (`itemNumbers[2]`) — so `focusValidationError`
 * reaches the cell with no mapping. A row's problem (malformed, or entered twice in this grid)
 * shows below the table, in red, naming the row; a number already used elsewhere is the
 * server's to report, on Submit.
 */
export function ItemNumberGrid({
  idPrefix,
  rows,
  onRows,
  disabled,
  elsewhere,
  max = ITEM_NUMBERS_MAX,
  maxReason = `A post may authorize at most ${ITEM_NUMBERS_MAX} items at a time.`,
}: {
  idPrefix: string;
  rows: ItemNumberRow[];
  onRows: (rows: ItemNumberRow[]) => void;
  disabled: boolean;
  /** Upper-cased numbers typed in the form's OTHER grids — another unit's card. */
  elsewhere?: ReadonlySet<string>;
  /** Lowered by a form whose OTHER grids share one request-wide ceiling. */
  max?: number;
  maxReason?: string;
}) {
  const problems = rows
    .map((_, index) => ({ index, problem: itemNumberProblem(rows, index, elsewhere) }))
    .filter((entry): entry is { index: number; problem: string } => entry.problem !== null);

  return (
    <div>
      <div className="flex min-h-5 items-center gap-1.5 pb-1.5">
        <p className="text-sm text-slate-700">
          Slots
          <span className="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
        </p>
        <HelpTip label={SLOTS_HELP} />
      </div>

      <p className="sr-only">Slots, required. {SLOTS_HELP}</p>

      <InlineAddTable
        headers={['Item number']}
        colWidths={[320]}
        flexColumn={0}
        rowCount={rows.length}
        onAddRow={() => onRows([...rows, blankItemNumberRow()])}
        addDisabled={
          disabled || rows.length >= max || rows.some((row) => row.itemNumber.trim().length === 0)
        }
        addDisabledReason={
          rows.length >= max
            ? maxReason
            : 'Type the item number in the current row before adding another.'
        }
        addButtonId={`${idPrefix}-add-slot`}
      >
        {rows.map((row, index) => (
          <InlineRow
            key={row.key}
            idx={index + 1}
            onRemove={() => {
              const next = rows.filter((other) => other.key !== row.key);

              // Never nothing: an empty grid has no row to press a `+` from.
              onRows(next.length > 0 ? next : [blankItemNumberRow()]);
            }}
          >
            <TdInput
              id={`${idPrefix}-item-numbers[${index}]`}
              ariaLabel={`Item number, row ${index + 1}`}
              placeholder="e.g. OSEC-DECSB-ADOF2-1-2004"
              value={row.itemNumber}
              onChange={(itemNumber) =>
                onRows(rows.map((other) => (other.key === row.key ? { ...other, itemNumber } : other)))
              }
              disabled={disabled}
            />
          </InlineRow>
        ))}
      </InlineAddTable>

      {problems.length > 0 ? (
        <ul className="mt-1.5 flex flex-col gap-0.5" role="alert">
          {problems.map(({ index, problem }) => (
            <li key={index} className="text-xs text-red-600">
              Row {index + 1}: {problem}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
